/* ═══════════════════════════════════════════════════════
   NYC Suite — Draft record projection

   The quote builder autosaves its in-progress state to Firestore
   (app/nycap-draft) after every edit. That state carries heavy, transient
   fields — base64 image previews (`dataUrl`) and in-memory File objects
   (`file`) on attachments — that must never go to Firestore:

     • Firestore caps a single string field at 1,048,487 bytes. One compressed
       photo preview blows past it → every autosave fails
       ([invalid-argument] "longer than 1048487 bytes").
     • Worse, the oversized payloads error the Write WebChannel at transport
       level; the SDK reconnects and requeues until it hits
       [resource-exhausted] "Write stream exhausted maximum allowed queued
       writes" — after which NO write from that tab lands, including Ingest
       (2026-08-21 incident: "Ingesting…" spinner forever).

   toDraftRecord() is the single lean projection every draft write uses.
   Previews survive reloads via the IndexedDB image cache (imageStorage.js),
   not Firestore.
   ═══════════════════════════════════════════════════════ */

/** Hard ceiling for one Firestore string field (bytes, UTF-8). */
export const FIRESTORE_MAX_FIELD_BYTES = 1048487;

/**
 * Autosave budget for the JSON-stringified draft. Well under the field cap so a
 * draft that is merely large (long notes, many line items) still saves, while
 * anything pathological is skipped instead of poisoning the write queue.
 */
export const DRAFT_MAX_BYTES = 900 * 1024;

/**
 * Lean attachment shape shared by every Firestore quote/draft write. Drops
 * `dataUrl` (base64 preview), `file` (in-memory File), and `bytes` (add-time
 * size check) — none of which belong in a document.
 */
export function leanAttachment(a) {
  return {
    id: a.id,
    name: a.name,
    type: a.type,
    size: a.size,
    url: a.url || null,
    storagePath: a.storagePath || null,
    aiRead: a.aiRead || false,
    extracted: a.extracted || "",
    includeInAddendum: a.includeInAddendum || false,
    caption: a.caption || "",
  };
}

export function leanAttachments(list) {
  return (list || []).map(leanAttachment);
}

/**
 * Project builder state to what the autosave draft may persist. Everything
 * except attachments passes through untouched; attachments are reduced to the
 * lean shape. Pure — never mutates `q`.
 */
export function toDraftRecord(q) {
  if (!q || typeof q !== "object") return q;
  return { ...q, attachments: leanAttachments(q.attachments) };
}

/** UTF-8 byte length of a value's JSON — what Firestore actually measures. */
export function draftByteLength(value) {
  const json = JSON.stringify(value);
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(json).length;
  return json.length;
}

/** Image previews held in memory on the builder state, for the local cache. */
export function draftImagePreviews(q) {
  return (q?.attachments || [])
    .filter(a => a && a.type === "image" && typeof a.dataUrl === "string" && a.dataUrl)
    .map(a => ({ id: a.id, dataUrl: a.dataUrl }));
}
